/**
 * Money on screen: one formatter, one rounding rule.
 *
 * Platform test round 4 (B10): the same spend read "$0.31" on a card and
 * "$0.32" in the header. antd's `Statistic` with `precision={2}` cuts the
 * third decimal off (0.318 → "0.31") while `toFixed(2)` rounds it; and each
 * page added the "$" itself, or an icon instead of it. Every amount goes
 * through here: cents, rounded half up (the API's rule), always with the
 * currency symbol. Hand a `Statistic` the formatted string, not a number
 * with a precision.
 */

function toFinite(value: string | number | null | undefined): number {
  const n = typeof value === "number" ? value : Number.parseFloat(value ?? "");
  return Number.isFinite(n) ? n : 0;
}

/** Round half up (away from zero) to `digits` decimals, free of float noise
 *  for the amounts a bill holds (0.315 must not come out as 0.31). */
function roundHalfUp(n: number, digits: number): number {
  const scale = 10 ** digits;
  const scaled = Math.abs(n) * scale;
  return (Math.sign(n) * Math.round(scaled + scaled * Number.EPSILON)) / scale;
}

/** "$1,234.50". Missing or unreadable input is "$0.00". */
export function formatMoney(value: string | number | null | undefined): string {
  const rounded = roundHalfUp(toFinite(value), 2);
  const text = Math.abs(rounded).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${rounded < 0 ? "-" : ""}$${text}`;
}

/** Least decimals a unit rate is shown with ("$0.0200"). */
const RATE_MIN_DECIMALS = 4;
/** Most decimals a unit rate is shown with. */
const RATE_MAX_DECIMALS = 12;

/**
 * A unit rate: four decimals, or as many as it takes to show two significant
 * digits of a smaller one ("$0.0000167", never "$0.0000" for a rate that is
 * not zero).
 */
export function formatRate(value: string | number | null | undefined): string {
  const n = toFinite(value);
  if (n === 0) return `$${(0).toFixed(RATE_MIN_DECIMALS)}`;
  const magnitude = Math.abs(n);
  let decimals = RATE_MIN_DECIMALS;
  if (magnitude < 10 ** -(RATE_MIN_DECIMALS - 1)) {
    // Two significant digits: 0.0000166667 → 7 decimals.
    decimals = Math.min(RATE_MAX_DECIMALS, 1 - Math.floor(Math.log10(magnitude)));
  }
  let text = magnitude.toFixed(decimals);
  if (decimals > RATE_MIN_DECIMALS) {
    // 0.00002 is "0.00002", not "0.000020".
    text = text.replace(/0+$/, "");
    const shown = text.split(".")[1]?.length ?? 0;
    if (shown < RATE_MIN_DECIMALS) text = magnitude.toFixed(RATE_MIN_DECIMALS);
  }
  return `${n < 0 ? "-" : ""}$${text}`;
}
